"use client";
import styles from "./toolsPara.module.css";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

interface HowToUseTextProps {
  toolName: string;
  // Legacy format support
  firstPata?: string;
  secondPara?: string;
  // New comprehensive format
  toolData?: {
    toolType?: string;
    introduction?: {
      para1?: string;
      para2?: string;
      para3?: string;
    };
    howToUse?: Array<{
      title: string;
      points: string[];
    }>;
    benefits?: Array<{
      title: string;
      description: string;
    }>;
    targetAudience?: Array<{
      title: string;
      description: string;
    }>;
    useCases?: Array<{
      title: string;
      description: string;
    }>;
    features?: Array<{
      title: string;
      description: string;
    }>;
    tips?: Array<{
      title: string;
      description: string;
    }>;
    faqs?: Array<{
      question: string;
      answer: string;
    }>;
    technicalInfo?: {
      accuracy?: string;
      security?: string;
      browserCompatibility?: boolean;
    };
    relatedTools?: Array<{
      name: string;
      link?: string;
      description: string;
    }>;
    conclusion?: string;
    contactLink?: string;
    aboutMasterUtils?: boolean;
  };
  // Old format support (for backward compatibility)
  howToUse?: string[];
  whyUse?: string;
  commonUseCases?: Array<{ title: string; description: string }>;
  features?: Array<{ title: string; description: string }>;
  faq?: Array<{ question: string; answer: string }>;
  tips?: string[];
  relatedTools?: Array<{ name: string; url: string; description: string }>;
}

export const HowToUseText = ({
  toolName,
  firstPata,
  secondPara,
  toolData,
  // Old format props for backward compatibility
  howToUse: oldHowToUse,
  whyUse,
  commonUseCases,
  features: oldFeatures,
  faq,
  tips: oldTips,
  relatedTools: oldRelatedTools,
}: HowToUseTextProps) => {
  const t = useTranslations("toolPara");
  // Legacy format support - if only firstPara/secondPara provided
  if ((firstPata || secondPara) && !toolData) {
    return (
      <div className={styles.howToParaMain}>
        <h1 className={styles.howToUse}>{toolName}: {t("howCanIUseIt")}</h1>
        <div className={styles.contentSection}>
          <div className={styles.contentText}>
            {firstPata && <p>{firstPata}</p>}
            {secondPara && (
              <>
                <br />
                <p>{secondPara}</p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // New comprehensive format
  if (!toolData) return null;

  return (
    <div className={styles.howToParaMain}>
      {/* Introduction */}
      <h1 className={styles.howToUse}>
        {toolName} - {t("freeOnlineTool")} {toolData.toolType || t("tool")}
      </h1>
      {toolData.introduction && (
        <section className={styles.contentSection}>
          <h2 className={styles.sectionTitle}>{t("introduction")}</h2>
          <div className={styles.contentText}>
            {toolData.introduction.para1 && <p>{toolData.introduction.para1}</p>}
            {toolData.introduction.para2 && (
              <>
                <br />
                <p>{toolData.introduction.para2}</p>
              </>
            )}
            {toolData.introduction.para3 && (
              <>
                <br />
                <p>{toolData.introduction.para3}</p>
              </>
            )}
          </div>
        </section>
      )}

      {/* How to Use */}
      {toolData.howToUse && toolData.howToUse.length > 0 && (
        <section className={styles.contentSection}>
          <h2 className={styles.sectionTitle}>{t("howToUse")} {toolName}</h2>
          <div className={styles.contentText}>
            <p>{t("usingToolSimple", { toolName })}</p>
            {toolData.howToUse.map((step, index) => (
              <div key={index} className={styles.stepContainer}>
                <strong>{index + 1}. {step.title}</strong>
                <ul className={styles.stepList}>
                  {step.points.map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
                </ul>
              </div>
            ))}
            <p>{t("processTakesSeconds")}</p>
          </div>
        </section>
      )}

      {/* Why Use */}
      {toolData.benefits && toolData.benefits.length > 0 && (
        <section className={styles.contentSection}>
          <h2 className={styles.sectionTitle}>{t("whyUse")} {toolName}?</h2>
          <div className={styles.contentText}>
            <h3 className={styles.subHeading}>{t("benefitsAndAdvantages")}</h3>
            <p>{t("ourToolOffersBenefits", { toolName })}</p>
            <ul className={styles.featuresList}>
              {toolData.benefits.map((benefit, index) => (
                <li key={index}>
                  <strong>{benefit.title}:</strong> {benefit.description}
                </li>
              ))}
            </ul>

            {toolData.targetAudience && toolData.targetAudience.length > 0 && (
              <>
                <h3 className={styles.subHeading}>{t("whoShouldUse")}</h3>
                <p>{t("thisToolPerfectFor")}</p>
                <ul className={styles.featuresList}>
                  {toolData.targetAudience.map((audience, index) => (
                    <li key={index}>
                      <strong>{audience.title}:</strong> {audience.description}
                    </li>
                  ))}
                </ul>
                <p>{t("whetherProfessional", { toolName })}</p>
              </>
            )}
          </div>
        </section>
      )}

      {/* Common Use Cases */}
      {toolData.useCases && toolData.useCases.length > 0 && (
        <section className={styles.contentSection}>
          <h2 className={styles.sectionTitle}>{t("commonUseCases")}</h2>
          <div className={styles.contentText}>
            <p>{t("popularWaysUse", { toolName })}</p>
            {toolData.useCases.map((useCase, index) => (
              <div key={index} className={styles.useCaseContainer}>
                <h3 className={styles.subHeading}>{index + 1}. {useCase.title}</h3>
                <p>{useCase.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Key Features */}
      {toolData.features && toolData.features.length > 0 && (
        <section className={styles.contentSection}>
          <h2 className={styles.sectionTitle}>{t("keyFeatures")}</h2>
          <div className={styles.contentText}>
            <p>{t("ourToolIncludes", { toolName })}</p>
            <ul className={styles.featuresList}>
              {toolData.features.map((feature, index) => (
                <li key={index}>
                  <strong>{feature.title}:</strong> {feature.description}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Tips and Best Practices */}
      {toolData.tips && toolData.tips.length > 0 && (
        <section className={styles.contentSection}>
          <h2 className={styles.sectionTitle}>{t("tipsAndBestPractices")}</h2>
          <div className={styles.contentText}>
            <p>{t("getMostOut", { toolName })}</p>
            {toolData.tips.map((tip, index) => (
              <div key={index} className={styles.tipContainer}>
                <h3 className={styles.subHeading}>{t("tip")} {index + 1}: {tip.title}</h3>
                <p>{tip.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQs */}
      {toolData.faqs && toolData.faqs.length > 0 && (
        <section className={styles.contentSection}>
          <h2 className={styles.sectionTitle}>{t("frequentlyAskedQuestions")}</h2>
          <div className={styles.faqContainer}>
            {toolData.faqs.map((faq, index) => (
              <div key={index} className={styles.faqItem}>
                <h3 className={styles.faqQuestion}>Q{index + 1}: {faq.question}?</h3>
                <p className={styles.faqAnswer}><strong>{t("answer")}:</strong> {faq.answer}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Technical Information */}
      {toolData.technicalInfo && (
        <section className={styles.contentSection}>
          <h2 className={styles.sectionTitle}>{t("technicalInformation")}</h2>
          <div className={styles.contentText}>
            {toolData.technicalInfo.accuracy && (
              <>
                <h3 className={styles.subHeading}>{t("accuracyAndReliability")}</h3>
                <p>{toolData.technicalInfo.accuracy}</p>
              </>
            )}
            {toolData.technicalInfo.security && (
              <>
                <h3 className={styles.subHeading}>{t("securityAndPrivacy")}</h3>
                <p>{toolData.technicalInfo.security}</p>
              </>
            )}
            {toolData.technicalInfo.browserCompatibility !== false && (
              <>
                <h3 className={styles.subHeading}>{t("browserCompatibility")}</h3>
                <p>{t("browserCompatibilityText", { toolName })}</p>
              </>
            )}
          </div>
        </section>
      )}

      {/* Related Tools */}
      {toolData.relatedTools && toolData.relatedTools.length > 0 && (
        <section className={styles.contentSection}>
          <h2 className={styles.sectionTitle}>{t("relatedTools")}</h2>
          <div className={styles.contentText}>
            <p>{t("youMightAlsoFind")}</p>
            <ul className={styles.featuresList}>
              {toolData.relatedTools.map((tool, index) => (
                <li key={index}>
                  {tool.link ? (
                    <Link href={tool.link} className={styles.relatedToolLink}>
                      <strong>{tool.name}:</strong>
                    </Link>
                  ) : (
                    <strong>{tool.name}:</strong>
                  )}{" "}
                  {tool.description}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Conclusion */}
      {toolData.conclusion && (
        <section className={styles.contentSection}>
          <h2 className={styles.sectionTitle}>{t("conclusion")}</h2>
          <div className={styles.contentText}>
            <p>{toolData.conclusion}</p>
            {toolData.contactLink && (
              <p>
                <strong>{t("haveQuestionsOrFeedback")}</strong>{" "}
                <Link href={toolData.contactLink} className={styles.contactLink}>
                  {t("contactUs")}
                </Link>{" "}
                - {t("contactUsSuffix")}
              </p>
            )}
          </div>
        </section>
      )}

      {/* About MasterUtils */}
      {toolData.aboutMasterUtils !== false && (
        <section className={styles.contentSection}>
          <h2 className={styles.sectionTitle}>{t("aboutMasterUtils")}</h2>
          <div className={styles.contentText}>
            <p>
              {t("aboutMasterUtilsText")}
            </p>
            <div className={styles.footerLinks}>
              <Link href="/">{t("browseAllTools")}</Link> | <Link href="/about">{t("aboutUs")}</Link> | <Link href="/privacy-policy">{t("privacyPolicy")}</Link> | <Link href="/contact">{t("contact")}</Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
